import SetNoScrBarVW from "./_SetNoScrBarVW";

const TableScroll = () => {
  SetNoScrBarVW();

  const draggedSet = new WeakSet<HTMLElement>();
  const listenerMap = new Map<
    HTMLElement,
    {
      mouseDown: (e: MouseEvent) => void;
      mouseMove: (e: MouseEvent) => void;
      mouseUp: (e: MouseEvent) => void;
      mouseLeave: (e: MouseEvent) => void;
    }
  >();

  // ================================
  //  シャドウ制御
  // ================================
  function updateTableWrapShadows(inner: HTMLElement): void {
    const scrollLeft = inner.scrollLeft;
    const scrollRight = inner.scrollWidth - inner.clientWidth - scrollLeft;
    const wrap = inner.parentElement;
    if (!wrap) return;

    wrap.classList.toggle("shadow-left", scrollLeft > 0);
    wrap.classList.toggle("shadow-right", scrollRight > 1);
  }

  // ================================
  //  幅計算関数（共通化）
  // ================================
  function updateMarginWidth(margin: HTMLElement, table: HTMLTableElement): void {
    const tableWidth = table.offsetWidth;
    const vwNoscrbar = "var(--vw-noscrbar)";

    // tableにも固定幅を付与
    table.style.width = `${tableWidth}px`;

    // 768px以下ではJS計算（負数補正あり）
    if (window.innerWidth <= 768) {
      const vw = window.innerWidth;
      const getvw342 = (342 / 390) * vw; // SCSSの getvw(342) 相当
      const extra = Math.max(0, (vw - getvw342) / 2);
      margin.style.width = `${tableWidth + extra}px`;
    } else {
      // PCはCSSのcalcでOK
      margin.style.width = `calc(${tableWidth}px + (${vwNoscrbar} * (1 - 0.8281)) / 2)`;
    }
  }

  // ================================
  //  構造作成 & 判定
  // ================================
  function setupTableWrapShadows(): void {
    const tables = document.querySelectorAll<HTMLTableElement>(
      "table.table-wrap-target"
    );

    tables.forEach((table) => {
      // すでにラップ済みならスキップ
      if (table.closest(".table-wrap")) return;

      const parent = table.parentElement;
      if (!parent) return;

      // ---- 判定 ----
      const parentWidth = parent.clientWidth;
      const tableWidth = table.offsetWidth;

      // 横スクロール不要 → width:100% を付与して終了
      if (tableWidth <= parentWidth + 1) {
        table.style.width = "100%";
        return;
      }

      // ---- 構築 ----
      const wrap = document.createElement("div");
      wrap.className = "table-wrap";

      const inner = document.createElement("div");
      inner.className = "table-inner";

      const margin = document.createElement("div");
      margin.className = "table-margin";

      // 構造：
      // .table-wrap
      //   └ .table-inner
      //        └ .table-margin
      //             └ table
      parent.insertBefore(wrap, table);
      wrap.appendChild(inner);
      inner.appendChild(margin);
      margin.appendChild(table);

      requestAnimationFrame(() => updateMarginWidth(margin, table));
    });
  }

  // ================================
  //  ドラッグスクロール制御
  // ================================
  function enableHorizontalDragScroll(container: HTMLElement): void {
    if (draggedSet.has(container)) return;

    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasMoved = false;

    const mouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDragging = true;
      hasMoved = false;
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      container.classList.add("dragging");
    };

    const mouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(walk) > 5) hasMoved = true;
      container.scrollLeft = scrollLeft - walk;
    };

    const mouseUp = (e: MouseEvent) => {
      container.classList.remove("dragging");
      if (!hasMoved) {
        const target = e.target as HTMLElement;
        if (target.tagName === "A" || target.closest("a")) return;
      }
      if (hasMoved) e.preventDefault();
      isDragging = false;
    };

    const mouseLeave = () => {
      isDragging = false;
      container.classList.remove("dragging");
    };

    container.addEventListener("mousedown", mouseDown);
    container.addEventListener("mousemove", mouseMove);
    container.addEventListener("mouseup", mouseUp);
    container.addEventListener("mouseleave", mouseLeave);

    listenerMap.set(container, { mouseDown, mouseMove, mouseUp, mouseLeave });
    draggedSet.add(container);

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      selection.removeAllRanges();
    }
  }

  function disableHorizontalDragScroll(container: HTMLElement): void {
    const listeners = listenerMap.get(container);
    if (!listeners) return;

    container.removeEventListener("mousedown", listeners.mouseDown);
    container.removeEventListener("mousemove", listeners.mouseMove);
    container.removeEventListener("mouseup", listeners.mouseUp);
    container.removeEventListener("mouseleave", listeners.mouseLeave);

    listenerMap.delete(container);
    draggedSet.delete(container);
  }

  // ================================
  //  カーソルに応じてドラッグ有効化
  // ================================
  function applyDragScrollIfGrabCursor(): void {
    const inners = document.querySelectorAll<HTMLElement>(
      ".table-wrap .table-inner"
    );
    inners.forEach((inner) => {
      const cursorStyle = getComputedStyle(inner).cursor;
      if (cursorStyle === "grab") {
        enableHorizontalDragScroll(inner);
      } else {
        disableHorizontalDragScroll(inner);
      }
    });
  }

  // ================================
  //  リサイズ時の再判定
  // ================================
  function handleResize(): void {
    document.querySelectorAll<HTMLTableElement>("table.table-wrap-target").forEach((table) => {
      const wrap = table.closest(".table-wrap");
      const parent = wrap ? wrap.parentElement : table.parentElement;
      if (!parent) return;

      const parentWidth = parent.clientWidth;
      const tableWidth = table.offsetWidth;

      // ✅ 横スクロール不要になったら解除＆width:100%
      if (wrap && tableWidth <= parentWidth + 1) {
        wrap.replaceWith(table);
        table.style.width = "100%";
        return;
      }

      // ✅ 横スクロール必要になったら再ラップ
      if (!wrap && tableWidth > parentWidth + 1) {
        setupTableWrapShadows();
        return;
      }

      // ✅ 維持している場合は幅だけ更新
      if (wrap) {
        const margin = wrap.querySelector(".table-margin") as HTMLElement;
        if (margin) requestAnimationFrame(() => updateMarginWidth(margin, table));
      }
    });

    // シャドウ更新とドラッグ再適用
    document.querySelectorAll<HTMLElement>(".table-wrap .table-inner").forEach((inner) => {
      updateTableWrapShadows(inner);
    });
    applyDragScrollIfGrabCursor();
  }

  // ================================
  //  初期化
  // ================================
  window.addEventListener("DOMContentLoaded", () => {
    setupTableWrapShadows();

    document.querySelectorAll<HTMLElement>(".table-wrap .table-inner").forEach((inner) => {
      updateTableWrapShadows(inner);
      inner.addEventListener("scroll", () => updateTableWrapShadows(inner));
    });

    applyDragScrollIfGrabCursor();
  });

  // ================================
  //  リサイズ処理登録
  // ================================
  window.addEventListener("resize", handleResize);
};

export default TableScroll;
